import { CONTRACTS_ENDPOINT, TENANT_SETTINGS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Contract } from "../../../../types/contracts";
import { Box, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useTranslation } from "react-i18next";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { useHasPermission } from "../../../../contexts/auth";
import { ContractEndDateDisplay, formatNumber } from "../../../../utils";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  DetailValue,
} from "../../../../components/ui/detailView";
import { StatusChip } from "../../../../components/ui/statusChip/statusChip.component";

type ContractBasicInfoProps = {
  previewId?: string | number;
};

export const ContractBasicInfo = ({ previewId }: ContractBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(CONTRACTS_ENDPOINT);
  const { get: getTenantSettings } = useCRUD(TENANT_SETTINGS_ENDPOINT);
  const [contract, setContract] = useState<Contract | null>(null);
  const [automationRunDay, setAutomationRunDay] = useState<number | null>(null);
  const [defaultCounterPriorityProviderDisplay, setDefaultCounterPriorityProviderDisplay] = useState<string | null>(null);
  const canViewClients = useHasPermission("settlement.view_client");
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchContract = async () => {
      if (entityId) {
        const response = await get(entityId);
        setContract(response.data);
      }
    };

    const handleContractUpdated = () => {
      fetchContract();
    };

    window.addEventListener("contractUpdated", handleContractUpdated);
    fetchContract();
    return () => {
      window.removeEventListener("contractUpdated", handleContractUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  useEffect(() => {
    const fetchAutomationRunDay = async () => {
      const response = await getTenantSettings("settings");
      setAutomationRunDay(response?.data?.automation_run_day ?? null);
      setDefaultCounterPriorityProviderDisplay(
        response?.data?.equipment_counter_priority_provider_display ?? null
      );
    };

    fetchAutomationRunDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!contract) return <DetailPage loading />;

  const settledChip = (
    <StatusChip
      label={contract.has_pending_settlements ? t("Unsettled") : t("Settled")}
      color={contract.has_pending_settlements ? "warning" : "success"}
    />
  );

  return (
    <DetailPage>
      <DetailHeader
        icon={<DescriptionOutlinedIcon />}
        title={contract.internal_name || contract.number}
        subtitle={contract.client?.short_name || contract.client?.name}
        chips={
          <>
            {settledChip}
            <StatusChip
              variant="outlined"
              label={contract.is_brutto ? t("Gross (Brutto)") : t("Net (Netto)")}
            />
          </>
        }
      />

      <DetailGrid>
        <DetailSection title={t("Basic Information")}>
          {contract.client && (
            <DetailRow label={t("Client Name")}>
              <EntityReferenceLink
                entityType="client"
                entityId={contract.client?.id}
                label={contract.client?.short_name || contract.client?.name || "—"}
                canPreview={canViewClients}
                stopPropagation={false}
              />
            </DetailRow>
          )}
          {contract.internal_name && (
            <DetailRow label={t("Internal name")} value={contract.internal_name} />
          )}
          {contract.number && <DetailRow label={t("Number")} value={contract.number} />}
          {contract.numeration_schema && (
            <DetailRow label={t("Numeration schema")} value={contract.numeration_schema?.name} />
          )}
          <DetailRow label={t("Settlement kind")} value={contract.settlement_kinds?.join(", ")} />
          <DetailRow label={t("Has pending settlements")} value={settledChip} />
          <DetailRow
            label={t("Inflation condition")}
            value={
              contract.inflation_condition_display?.trim() &&
              `${contract.inflation_condition_display.trim()}${
                contract.inflation_rate_percent != null
                  ? ` (${contract.inflation_rate_percent}%)`
                  : ""
              }`
            }
          />
          <DetailRow
            label={t("Settlement Automation")}
            value={`${contract.automation_level_display?.trim() || t("Manual")}${
              contract.automation_level && automationRunDay
                ? ` ${t("(runs on day {{day}} of each month)", { day: automationRunDay })}`
                : ""
            }`}
          />
          <DetailRow
            label={t("Counter priority provider")}
            value={
              contract.counter_priority_provider_display?.trim()
                ? contract.counter_priority_provider_display
                : defaultCounterPriorityProviderDisplay
                ? t("Inherits tenant default ({{provider}})", { provider: defaultCounterPriorityProviderDisplay })
                : t("Inherits tenant default (not set)")
            }
          />
          <DetailRow
            label={t("Values stored as")}
            value={contract.is_brutto ? t("Gross (Brutto)") : t("Net (Netto)")}
          />
        </DetailSection>

        <DetailSection title={t("Contract Dates")}>
          {contract.start_date && <DetailRow label={t("Start Date")} value={contract.start_date} />}
          <DetailRow label={t("End Date")}>
            <DetailValue>
              <ContractEndDateDisplay
                endDate={contract.end_date}
                indefiniteAfterEndDate={contract.indefinite_after_end_date}
                t={t}
              />
            </DetailValue>
          </DetailRow>
          {contract.issuance_date && (
            <DetailRow label={t("Issuance Date")} value={contract.issuance_date} />
          )}
          {contract.signature_date && (
            <DetailRow label={t("Signature Date")} value={contract.signature_date} />
          )}
        </DetailSection>
      </DetailGrid>

      {contract.terms && contract.terms.length > 0 && (
        <>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mt: 1 }}>
            {t("Contract Terms Details")}
          </Typography>
          <DetailGrid>
            {contract.terms.map((term) => (
              <DetailSection
                key={term.id}
                title={
                  term.equipments?.map((equipment) => equipment.full_name).filter(Boolean).join(", ") ||
                  t("Contract Position")
                }
              >
                <DetailRow label={t("Equipment")}>
                  {term.equipments?.length ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {term.equipments.map((equipment) => (
                        <EntityReferenceLink
                          key={equipment.id}
                          entityType="equipment"
                          entityId={equipment.id}
                          label={equipment.full_name || "—"}
                          canPreview={canViewEquipment}
                          stopPropagation={false}
                        />
                      ))}
                    </Box>
                  ) : null}
                </DetailRow>
                <DetailRow label={t("Kind")} value={term.kind_display} />
                <DetailRow label={t("Settlement Cycle")} value={term.settlement_cycle_display} />
                <DetailRow label={t("Settlement kind")} value={term.settlement_kind_display} />
                <DetailRow label={t("Rent Price")} value={`${formatNumber(term.rent_price)} zł`} />
                <DetailRow label={t("Prices")} alignTop>
                  {term.prices?.length ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      {term.prices.map((price, index) => (
                        <DetailValue key={index}>
                          {price.price ? `${formatNumber(price.price)} zł` : "—"}
                          {price.included_in_rent ? ` (${t("Included in Rent")}: ${price.included_in_rent})` : ""}
                        </DetailValue>
                      ))}
                    </Box>
                  ) : null}
                </DetailRow>
              </DetailSection>
            ))}
          </DetailGrid>
        </>
      )}

      <DetailMetadata createdAt={contract.created_at} updatedAt={contract.updated_at} />
    </DetailPage>
  );
};
